import _ from 'lodash'
import { utils as tkUtils } from '@adc/ui-core'
import flatten from 'flat'

const { formatTitleCase } = tkUtils.formatters

const OPEN_DETAILS = 'Open Drawer for More Details'

const requiredNestedFields = [
  'created_date',
  'created_by',
  'updated_by',
  'updated_date',
]
/**
 * Method to translate nested objects from the audit history API (/resi/audit) to
 * the objects that are ideal for audit history UI
 *
 * @param {Array} excludedFields,:  an Array of strings that identify the fields that
 * are intended to not be displayed in the table
 * @param {Array} rawApiValues: an Array of strings that identify the fields where the values
 * for each field is expected to be the value's primitive type
 * @param {Object} fieldLabels: an Object that identifies that columns that has a desired label
 * instead of a label based on the api reference.
 * @param {Array} includeFields: an Array of strings that identify fields that are
 * intended to be included in the table.
 * @param {String} openDrawerDesc: a String that a desired label for the header of the audit history drawer
 *
 * @returns an Array of Objects that are ideal for the auditTableColumnDefinitions
 */
export const apiToUiAuditDataRowsTranslation = (
  excludedFields,
  rawApiValues,
  fieldLabels = {},
  includeFields = [],
  openDrawerDesc = OPEN_DETAILS
) => {
  return dataRows => {
    if (!dataRows) return []

    if (includeFields.length) {
      const translatedDataRows = translateProperties(
        dataRows.filter(row => !excludedFields.includes(row.field)),
        includeFields
      )
      dataRows = !translatedDataRows ? [] : translatedDataRows
    }

    const formatField = getFormatFieldFunction(rawApiValues, openDrawerDesc)
    return dataRows
      .filter(row => !excludedFields.includes(row.field))
      .map(row => {
        const newRow = _.cloneDeep(row)

        const { reg: to, drawer: drawerTo } = formatField(row.to, row.field)
        const { reg: from, drawer: drawerFrom } = formatField(
          row.from,
          row.field
        )

        newRow.to = to
        newRow.drawerTo = drawerTo
        newRow.from = from
        newRow.drawerFrom = drawerFrom

        const fieldLabel = fieldLabels[row.field]
        newRow.field = fieldLabel
          ? fieldLabel.label
          : formatTitleCase(row.field)

        return newRow
      })
  }
}

/**
 * Currying helper method to format the fields for the audit history drawer
 *
 * @param {Array} rawApiValues: an Array of strings that identify the fields where the values
 * for each field is expected to be the value's primitive type
 * @param {String} openDrawerDesc: a String that a desired label for the header of the audit history drawer
 *
 * @returns an Object includes content and header for a the audit history drawer
 */
const getFormatFieldFunction =
  (rawApiValues, openDrawerDesc) => (item, field) => {
    let reg, drawer

    if (item === undefined) item = ''

    if (_.isArray(item)) {
      drawer = JSON.stringify(item)
      reg = openDrawerDesc
    } else {
      const shouldUseRawApiValue = _.some(rawApiValues, el =>
        _.includes(field, el)
      )
      reg = shouldUseRawApiValue ? item : formatTitleCase(item)
      drawer = shouldUseRawApiValue ? item : formatTitleCase(item)
    }

    return { reg, drawer }
  }
/**
 * Method to get fields from nested objects
 *
 * @param {Array} rowData: an Array of history objects created by the auditTable
 * reducer methods.
 * @param {Array} includeFields: an Array of strings that identify fields that are
 * intended to be included in the table
 * @returns an array of Objects that flattened to be ideal for the auditTableColumnDefinitions
 */
const translateProperties = (rowData, includeFields) => {
  return rowData.reduce((acc, propertyData) => {
    // Non Nested
    if (
      !Array.isArray(propertyData.to) &&
      !Array.isArray(propertyData.from) &&
      includeFields.includes(propertyData.field)
    ) {
      acc.push(propertyData)
      return acc
    } else {
      if (includeFields.includes(propertyData.field)) {
        propertyData.to.forEach((toData, i) => {
          const fromData = _.isNil(propertyData.from)
            ? []
            : propertyData.from[i]
          // Translate deepest level properties to the first level
          const resultFromData = translatePropertyObjectData(fromData, [
            ...includeFields,
            ...requiredNestedFields,
          ])
          const resultToData = translatePropertyObjectData(toData, [
            ...includeFields,
            ...requiredNestedFields,
          ])
          // Determine the differences and omit required nested fields
          const diffField = getObjectDiff(resultFromData, resultToData).filter(
            field => {
              if (!requiredNestedFields.includes(field)) {
                return field.replace(/\./g, '___')
              }

              return undefined
            }
          )
          const name = findPropertyNameByRegex(resultToData, /name/i)
          const hasBeenUpdated = result =>
            result.updated_by && result.updated_date
          const diffPropertyObjs = diffField
            .reduce((result, field) => {
              result.push({
                field,
                from: resultFromData[field],
                to: resultToData[field],
                created_date: resultToData.created_date,
                created_by: resultToData.created_by,
                updated_date: propertyData.updated_date,
                updated_by: propertyData.updated_by,
                ...(!_.isNil(name) && { name: resultToData[name] }),
              })
              return result
            }, [])
            .filter(hasBeenUpdated)
          acc = [...acc, ...diffPropertyObjs]
        })
      }
      return acc
    }
  }, [])
}
/**
 * Helper method intended to compare two objects to compare the differences between property values.
 *
 * @param {Object} obj1: an Object created by the translateProperties method
 * @param {Object} obj2: an Object created by the translateProperties method
 *
 * @returns An array of strings where the strings identify properties that have different values between
 * obj1 and obj2
 */
const getObjectDiff = (obj1, obj2) => {
  if (_.isNil(obj1) || _.isNil(obj2)) {
    return []
  }
  return Object.keys(obj1).reduce((result, key) => {
    if (!(key in obj2)) {
      result.push(key)
    } else if (_.isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key)
      result.splice(resultKeyIndex, 1)
    }
    return result
  }, Object.keys(obj2))
}
/**
 * Recursive helper method to translate a nested object from the audity history api to
 * flattened version that is ideal for the auditTableColumnDefinition.
 *
 * The includeFieldList is intended to identify fields to be included in the audit table.
 *
 * @param {Object} obj: a nested object provided by audit history api
 * @param {Array} includeFieldList: an array of strings that identify which properties to not be recursed
 * and be included in the resulting object.
 * Examples of fields to include where recursion is or not necessary to get the requested value for
 * the audit history drawer
 * fields: [
 * // Exact field to recurse to, which is a property of a nested object
 * 'configuration.simple_boolean_field',
 * // Specify base entrypoint to recurse to render an object or array of values.
 * 'address.*'
 * ]
 * @returns an Object that is ideal for the auditTableColumnDefinition
 */
const translatePropertyObjectData = (propertyObj, includeFieldList = []) => {
  // Identify fields that are requesting all the nested properties
  const allFieldList = includeFieldList.filter(field => field.includes('*'))

  const fieldList = includeFieldList.map(field => field.replace(/\./g, '___'))
  // Flatten all keys with a '___' delimeter because more unlikely there will duplicate keys
  const flattenPropObj = !_.isNil(propertyObj)
    ? flatten(propertyObj, { delimiter: '___', safe: true })
    : {}

  const result = {}
  const recurseObj = (propertyObj, currentKey) => {
    for (const key in propertyObj) {
      const value = propertyObj[key]
      if (value !== undefined) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          recurseObj(value, key)
        } else if (fieldList.find(e => e === key)) {
          result[key] = value
        }
      }
    }
  }
  recurseObj(flattenPropObj)
  // allFieldList condition is intended to handle the field.* selection where
  // the intention is to not display only one value, but an entire object.
  if (allFieldList) {
    const allFields = allFieldList.map(field => field.replace(/\.\*/g, ''))
    const getAllFieldData = _.pick(propertyObj, allFields)
    Object.keys(getAllFieldData).forEach(k => {
      result[k] = [getAllFieldData[k]]
    })
  }

  return result
}
/**
 * Helper method to return a key of an object with a regular expression.
 *
 * @param {Object} obj1: An object that is intended search for particular property data key
 * @param {Regular Expression Literal} r: regular expression literal which consists of a
 * pattern enclosed between slashes
 *
 * @returns a String of the key of an object
 */
const findPropertyNameByRegex = (obj, pattern) =>
  _.isNil(obj) ? undefined : Object.keys(obj).find(key => key.match(pattern))
